import { styled } from '@mui/material/styles';
import { Paper, Typography, Box, IconButton, Tooltip } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

import { ClientAddress } from '../../../data/models/client.model';
import Address from './Address';

const PREFIX = 'ClientAddresses';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  addresses: `${PREFIX}-addresses`,
  addAddressButton: `${PREFIX}-addAddressButton`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiTypography-root': {
    color: `${theme.palette.lightTextPrimary.main} !important`,
  },

  [`&.${classes.container}`]: {
    flex: 1,
    minWidth: 300,
  },

  [`& .${classes.details}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    padding: theme.spacing(3, 4),
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    lineHeight: '32px',
    padding: 0,
    letterSpacing: 0,
  },

  [`& .${classes.addresses}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 4, 3, 4),
  },

  [`& .${classes.addAddressButton}`]: {
    padding: 0,
  },
}));

interface ClientAddressesProps {
  clientAddresses?: ClientAddress[];
  addAddress: () => void;
  editAddress: (address: ClientAddress) => void;
  activateAddress: (address: ClientAddress) => void;
  deactivateAddress: (address: ClientAddress) => void;
  deleteAddress: (address: ClientAddress) => void;
  readonly: boolean;
}

export default function ClientAddresses({
  clientAddresses,
  addAddress,
  editAddress,
  activateAddress,
  deactivateAddress,
  deleteAddress,
  readonly,
}: ClientAddressesProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.addresses')}
          </Typography>
          {!readonly && !isSuperReaderUser && (
            <Tooltip title={t('client.addAddress')}>
              <span>
                <IconButton
                  data-testid="add-address"
                  onClick={addAddress}
                  className={classes.addAddressButton}
                  color="primary"
                  size="large">
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {!!clientAddresses?.length && (
          <Box className={classes.addresses}>
            {clientAddresses?.map((address) => (
              <Address
                clientAddress={address}
                key={address.clientAddressUUID}
                editAddress={editAddress}
                activateAddress={activateAddress}
                deactivateAddress={deactivateAddress}
                deleteAddress={deleteAddress}
                readonly={readonly}
              />
            ))}
          </Box>
        )}
      </Paper>
    </StyledBox>
  );
}
